import { ApplicationRef, Injectable, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CheckForUpdateService {
  constructor(
    private appRef: ApplicationRef,
    private updates: SwUpdate
  ) {
    if (this.updates.isEnabled) {
      this.listenForUpdates();

      this.updates.checkForUpdate().then((result) => {
        if (!result) {
          this.autoReloadPage.set(false);
          this.showUpdateModal.set(false);
        } else {
          this.showUpdateModal.set(true);
        }
      });
    } else {
      this.autoReloadPage.set(false);
    }
  }

  showUpdateModal = signal<boolean>(false);
  autoReloadPage = signal<boolean>(true);
  showVersionUpgradeHeader = signal<boolean>(false);

  init() {
    // Allow the app to stabilize first, before starting
    // polling for updates with `interval()`.

    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const everyHour$ = interval(60 * 60 * 1000);
    const everyHourOnceAppIsStable$ = concat(appIsStable$, everyHour$);

    everyHourOnceAppIsStable$.subscribe((value) => {
      console.log('Checking for updates...');
      if (this.updates.isEnabled) {
        this.updates.checkForUpdate();
      }
    });
  }

  listenForUpdates() {
    if (this.updates.isEnabled) {
      this.updates.versionUpdates.subscribe((event) => {
        switch (event.type) {
          case 'VERSION_DETECTED':
            console.log(`New version detected: ${event.version.hash}`);
            this.autoReloadPage.set(true);
            break;
          case 'VERSION_READY':
            console.log(`New version ready.`);
            if (this.autoReloadPage()) {
              console.log(`Reloaded.`);
              window.location.reload();
            } else {
              this.showVersionUpgradeHeader.set(true);
            }
            this.showUpdateModal.set(false);
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.error(`Failed to install version ${event.version.hash}: ${event.error}`);
            this.handleInstallationFailure();
            break;
        }
      });
    }
  }

  handleInstallationFailure() {
    console.error('Hash mismatch or other installation failure occurred.');
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
        window.location.reload();
      });
    }
  }
}
